import { Controller } from "@hotwired/stimulus"

const PIN_LENGTH = 4
const DEBUG = true
const log = (...args) => DEBUG && console.debug("[PinEntry]", ...args)

const FILLED_TOKENS = ["bg-primary-500", "border-2", "border-primary-500"]
const EMPTY_TOKENS = ["border-2", "border-gray-300", "dark:border-gray-600", "bg-transparent"]

function parseClassTokens(str) {
  if (!str || typeof str !== "string") return []
  const tokens = str.trim().split(/\s+/).filter(Boolean)
  const bad = tokens.filter((t) => /\s/.test(t))
  if (bad.length) console.warn("[PinEntry] token contains whitespace, skipping:", bad)
  return tokens.filter((t) => !/\s/.test(t))
}

/**
 * Kiosk-style PIN entry: 4 circles, keypad, hidden input for keyboard.
 * - appendDigit(d), backspace(), clear()
 * - Auto-submit at 4 digits
 * - Shake + error on failure
 */
export default class extends Controller {
  static targets = ["dot", "dotsContainer", "keypad", "status", "pinInput"]
  static values = {
    submitUrl: String,
    extraParams: Object,
    formSelector: String,
    filledClass: { type: String, default: "bg-primary-500" },
    emptyClass: { type: String, default: "border-2 border-gray-300 dark:border-gray-600" },
  }

  connect() {
    log("connect")
    this.pin = ""
    this.statusState = "idle"
    this.updateDisplay()
    this.boundKeydown = this.handleDocumentKeydown.bind(this)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
  }

  open() {
    log("opened")
    this.pin = ""
    this.statusState = "idle"
    this.syncInputValue()
    this.updateDisplay()
    this.setStatus("")
    this.setKeypadDisabled(false)
    document.addEventListener("keydown", this.boundKeydown)
    requestAnimationFrame(() => {
      this.focusInput()
      log("focus input ok?", document.activeElement === this.pinInputTarget)
    })
  }

  close() {
    document.removeEventListener("keydown", this.boundKeydown)
  }

  focusInput() {
    if (this.hasPinInputTarget) {
      this.pinInputTarget.focus()
    }
  }

  syncInputValue() {
    if (this.hasPinInputTarget) {
      this.pinInputTarget.value = this.pin
    }
  }

  /** Handle keydown on the hidden input (keyboard entry) */
  onKeydown(e) {
    log("keydown key=", e.key)
    if (e.key === "Escape") {
      e.preventDefault()
      this.dispatch("cancel", { prefix: "pin-entry" })
      return
    }
    if (this.statusState === "submitting") {
      e.preventDefault()
      return
    }
    if (e.key === "Backspace") {
      e.preventDefault()
      this.appendDigit("⌫")
      return
    }
    const digit = /^[0-9]$/.exec(e.key)
    if (digit) {
      e.preventDefault()
      this.appendDigit(digit[0])
    }
  }

  /** Handle input event (e.g. paste) */
  onInput(e) {
    const raw = (e.target.value || "").replace(/\D/g, "").slice(0, PIN_LENGTH)
    this.pin = raw
    this.syncInputValue()
    this.updateDisplay()
    if (this.pin.length === PIN_LENGTH) this.submitPin()
  }

  /** Document keydown for Escape when focus might be elsewhere */
  handleDocumentKeydown(e) {
    if (e.key === "Escape") {
      this.dispatch("cancel", { prefix: "pin-entry" })
    }
  }

  appendDigit(d) {
    if (d === "" || d === "⌫") {
      this.pin = this.pin.slice(0, -1)
      log("backspace pin length=", this.pin.length)
    } else if (/^[0-9]$/.test(d) && this.pin.length < PIN_LENGTH) {
      this.pin += d
      log("pin updated length=", this.pin.length)
    }
    this.syncInputValue()
    this.updateDisplay()
    if (this.pin.length === PIN_LENGTH) {
      log("[StaffSwitch] submit pin length=4")
      this.submitPin()
    }
  }

  press(event) {
    event.preventDefault()
    if (this.statusState === "submitting") return
    const btn = event.currentTarget
    if (!btn?.dataset?.key) return
    const key = String(btn.dataset.key)
    log("press digit=", key)
    this.appendDigit(key)
  }

  clear() {
    if (this.statusState === "submitting") return
    log("clear")
    this.pin = ""
    this.syncInputValue()
    this.updateDisplay()
    this.setStatus("")
  }

  updateDisplay() {
    if (!this.hasDotTarget) return
    const filledTokens = parseClassTokens(this.filledClassValue)
    const emptyTokens = parseClassTokens(this.emptyClassValue)
    const addWhenFilled = filledTokens.length ? filledTokens : FILLED_TOKENS
    const addWhenEmpty = emptyTokens.length ? emptyTokens : EMPTY_TOKENS
    this.dotTargets.forEach((el, i) => {
      const filled = i < this.pin.length
      if (filled) {
        addWhenEmpty.forEach((t) => el.classList.remove(t))
        addWhenFilled.forEach((t) => el.classList.add(t))
        el.classList.remove("bg-transparent")
      } else {
        addWhenFilled.forEach((t) => el.classList.remove(t))
        addWhenEmpty.forEach((t) => el.classList.add(t))
        el.classList.add("bg-transparent")
      }
    })
  }

  async submitPin() {
    if (this.statusState === "submitting") return
    const pin = this.pin
    log("submit pin length:", pin.length)

    const evt = new CustomEvent("pin-entry:before-submit", {
      detail: { pin, blockReason: null },
      cancelable: true,
      bubbles: true,
    })
    this.element.dispatchEvent(evt)
    if (evt.defaultPrevented) {
      const msg = evt.detail?.blockReason || "Cannot submit"
      this.setStatus(msg, true)
      setTimeout(() => {
        this.pin = ""
        this.syncInputValue()
        this.updateDisplay()
        this.setStatus("")
      }, 2000)
      return
    }

    this.statusState = "submitting"
    this.setKeypadDisabled(true)
    this.setStatus("Checking…", false)

    const token = document.querySelector("[name='csrf-token']")?.content
    const formData = new FormData()
    if (token) formData.append("authenticity_token", token)
    formData.append("pin", pin)

    if (this.hasFormSelectorValue && this.formSelectorValue) {
      const form = document.querySelector(this.formSelectorValue)
      if (form) {
        for (const [k, v] of new FormData(form)) {
          if (k !== "pin" && k !== "authenticity_token") formData.append(k, v)
        }
      }
    } else {
      const extra = this.extraParamsValue || {}
      Object.entries(extra).forEach(([k, v]) => {
        if (v != null) formData.append(k, String(v))
      })
    }

    try {
      const res = await fetch(this.submitUrlValue, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      })
      const data = await res.json().catch(() => ({}))
      const ok = data.ok === true || data.success === true
      log("[StaffSwitch] response ok=", ok, data)
      if (ok && data.redirect_url) {
        this.dispatch("success", { prefix: "pin-entry", detail: data })
        log("[StaffSwitch] reloading as new staff")
        if (typeof Turbo !== "undefined" && Turbo.visit) {
          Turbo.visit(data.redirect_url, { action: "replace" })
        } else {
          window.location.href = data.redirect_url
        }
        return
      }
      this.handleError(data.error || "Invalid PIN")
    } catch (err) {
      log("submit fetch error", err)
      this.handleError("Something went wrong. Please try again.")
    }
  }

  handleError(message) {
    this.statusState = "error"
    this.pin = ""
    this.syncInputValue()
    this.updateDisplay()
    this.setKeypadDisabled(false)
    this.statusState = "idle"
    this.showShake()
    this.setStatus(message, true)
    this.focusInput()
    setTimeout(() => this.setStatus(""), 2500)
  }

  showShake() {
    const targets = []
    if (this.hasDotsContainerTarget) targets.push(this.dotsContainerTarget)
    if (this.hasKeypadTarget) targets.push(this.keypadTarget)
    if (targets.length === 0) targets.push(this.element)
    targets.forEach((el) => {
      el.style.animation = "kiosk-pin-shake 0.5s ease-in-out"
      el.addEventListener("animationend", () => { el.style.animation = "" }, { once: true })
    })
  }

  setStatus(text, isError = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-red-600", isError)
    this.statusTarget.classList.toggle("dark:text-red-400", isError)
    this.statusTarget.classList.toggle("text-gray-500", !isError)
    this.statusTarget.classList.toggle("dark:text-gray-400", !isError)
  }

  setKeypadDisabled(disabled) {
    if (this.hasKeypadTarget) {
      this.keypadTarget.querySelectorAll("button").forEach((btn) => {
        btn.disabled = disabled
      })
    }
  }

  dispatch(name, { prefix = "pin-entry" } = {}) {
    const evt = new CustomEvent(`${prefix}:${name}`, { bubbles: true })
    this.element.dispatchEvent(evt)
  }
}
